import {CSSProperties, ReactElement, useEffect, useRef, useState} from "react";
import {
  IParentSize, IProduction, IStrandProduction
} from "../../../types";
import {
  formatDate, saveZipAs,
} from "../../../system/utils";
import {
  CircularProgress, Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from "@mui/material";
import StyledTableRow from "../../../components/styledTableRow";
import IconButton from "@mui/material/IconButton";
import TableContainer from "../../../components/tableContainer";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { IMillProductDetail } from "../types";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { getPalette } from "../../../configuration/configuration";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import TimelineIcon from "@mui/icons-material/Timeline";
import Detail from "../../../components/dialogs/detail";
import ProductTrends from "../components/dialogPanels/productTrends";
import {
  exportByBillets,
  exportByMachine,
  exportByOrder,
  IBilletData, IDataProcessed, IProdAggregate,
  IProductionMachineData
} from "./libraries/qrExports";
import { useAsyncFetch } from "../../../system/useFetch";
import { ICatalogCategory, ICategoryField } from "../../config/types";

const inchFactor = 0.0393701;

interface IProps {
  productionData: IProduction[];
  products: IMillProductDetail[];
  parentSize: IParentSize;
}

interface IDetailDialog {
  open: boolean;
  item: IBilletData | null;
}

interface IBilletsRowComponentProps {
  open: boolean;
  billets: IBilletData[];
}

interface IProductionMachineRowComponentProps {
  data: IProductionMachineData;
}

const variableMapping: Record<string, string> = {
  "CC1.RM.RUFM.STD04.STR1.BILLET_TEMP": "Temp. Stand 4",
  "CC1.RM.WB1.STR1.ACTUAL_ENTRY_TEMP": "Temp. Waterbox",
  "CC1.RM.WB2.STR1.ACTUAL_LH_TEMP": "Temp. Laying Head",
  "CC1.RM.RUFM.STD04.STR2.BILLET_TEMP": "Temp. Stand 4",
  "CC1.RM.WB1.STR2.ACTUAL_ENTRY_TEMP": "Temp. Waterbox",
  "CC1.RM.WB2.STR2.ACTUAL_LH_TEMP": "Temp. Laying Head"
};

const fieldMapping: Record<string, string> = {
  // "CC1.RM.RUFM.STD04.STR1.BILLET_TEMP": "Temp. Stand 4",
  // "CC1.RM.WB1.STR1.ACTUAL_ENTRY_TEMP": "Temp. Waterbox",
  "CC1.RM.WB2.STR1.ACTUAL_LH_TEMP": "Temp Laying Head",
  // "CC1.RM.RUFM.STD04.STR2.BILLET_TEMP": "Temp. Stand 4",
  // "CC1.RM.WB1.STR2.ACTUAL_ENTRY_TEMP": "Temp. Waterbox",
  "CC1.RM.WB2.STR2.ACTUAL_LH_TEMP": "Temp Laying Head"
};


const style00 = (opacity: number): CSSProperties => ({
  backgroundColor: `rgba(156, 170, 177, ${opacity})`
});

const style01 = (opacity: number, error = false): CSSProperties => ({
  width: 80,
  maxWidth: 80,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(144, 202, 249, ${opacity})`
});

const style02 = (opacity: number, error = false): CSSProperties => ({
  width: 80,
  maxWidth: 80,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(128, 203, 196, ${opacity})`
});

const style03 = (opacity: number, error = false): CSSProperties => ({
  width: 80,
  maxWidth: 80,
  backgroundColor: error ? `rgba(255, 0, 0, ${1})` : `rgba(165, 214, 167, ${opacity})`
});

interface IRowProps {
  index: number;
  row: IDataProcessed;
}

const specDictionary: Record<string, ICategoryField[]> = {};

const getRecipeBackground = (recipe: string | null) => recipe && specDictionary[recipe] && specDictionary[recipe].length > 0 ? "#424242" : "red";


const evalError = (value: number | null, min: number | null, max: number | null) => {
  if (!value) {
    return null;
  }

  if (min && value < min) {
    return `${value} < ${min}`;
  }

  return max && value > max ? `${value} < ${max}` : null;
};

const evalAvg = (count: number, sum: number) => count ? sum / count : null;

const updateAggregate = (
  code: string,
  aggregate:  Record<string, IProdAggregate>,
  billetAggregate: IProdAggregate) => {
  const record = aggregate[code];
  const error = billetAggregate.errors.length > 0 ? billetAggregate.errors[0] : null;

  if (!record) {
    aggregate[code] = {
      min: billetAggregate.min,
      max: billetAggregate.max,
      sum: billetAggregate.sum,
      count: billetAggregate.count,
      specMin: billetAggregate.specMin,
      specMax: billetAggregate.specMax,
      errors: error ? [ error ] : []
    };
  } else {
    record.min = Math.min(record.min, billetAggregate.min);
    record.max = Math.max(record.max, billetAggregate.max);
    record.sum += billetAggregate.sum;
    record.count += billetAggregate.count;
    if (record.specMin !== billetAggregate.specMin) {
      record.specMin = null;
    }
    if (record.specMax !== billetAggregate.specMax) {
      record.specMax = null;
    }
    if (error) {
      record.errors.push(error);
    }
  }
};

const BilletsRowComponent = ({ billets, open }: IBilletsRowComponentProps): ReactElement => {
  const [openDetail, setOpenDetail] = useState<IDetailDialog>({ open: false, item: null });

  const onShowCharts = (item: IBilletData) => {
    setOpenDetail({ open: true, item });
  };

  return (
    <>
      <StyledTableRow>
        <TableCell style={{ padding: 0, border: "none" }} colSpan={23} className="table-cell">
          <Collapse in={open} timeout="auto" unmountOnExit={true}>
            <Table stickyHeader={true} size={"small"} >
              <TableHead sx={{ backgroundColor: "#b0bec5" }}>
                <TableRow >
                  <TableCell align="center" style={{ ...style00(.5), width: 100 }}/>
                  <TableCell align="center" style={style00(.5)}>Recipe</TableCell>
                  <TableCell align="center" style={style00(.5)}>Billet</TableCell>
                  <TableCell align="center" style={style00(.5)}>Length</TableCell>
                  <TableCell align="center" style={style00(.5)}>Pressure</TableCell>
                  <TableCell align="center" style={style00(.5)}>Weight</TableCell>

                  <TableCell align="center" style={style01(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style01(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style01(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style01(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style01(.5)}>AVG</TableCell>

                  <TableCell align="center" style={style02(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style02(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style02(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style02(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style02(.5)}>AVG</TableCell>

                  <TableCell align="center" style={style03(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style03(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style03(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style03(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style03(.5)}>AVG</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                { billets.map(billetData => {
                  const tempStand4Billet = billetData.aggregateRecord["Temp. Stand 4"];
                  const tempWaterBoxBillet = billetData.aggregateRecord["Temp. Waterbox"];
                  const tempLayingHeadBillet = billetData.aggregateRecord["Temp. Laying Head"];
                  const tempStand4Errors = tempStand4Billet && tempStand4Billet.errors.length > 0;
                  const tempWaterBoxErrors = tempWaterBoxBillet && tempWaterBoxBillet.errors.length > 0;
                  const tempLayingHeadErrors = tempLayingHeadBillet && tempLayingHeadBillet.errors.length > 0;
                  const compactingLength = (billetData.compactingLength ?? 0 ) * inchFactor;

                  let status = billetData.isCobble || billetData.isCutUp ? 2 : 1;
                  if (status === 1 && !billetData.weight) {
                    status = 0;
                  }

                  return (
                    <StyledTableRow key={`billet-${billetData.productionDetailID}`} hover={true} role="checkbox" tabIndex={-1}>
                      <TableCell align="right" style={{ width: 50 }}>
                        <IconButton size={"small"} onClick={() => onShowCharts(billetData)}>
                          <TimelineIcon/>
                        </IconButton>
                        {status === 0 &&
                          <Tooltip title="Pending">
                            <ErrorOutlineIcon style={{
                              color: getPalette().statusColor.info.background,
                              verticalAlign: "center",
                              marginLeft: "0.5em"
                            }}/>
                          </Tooltip>}
                        {status === 1 &&
                          <Tooltip title="Processed">
                            <CheckCircleOutlineIcon style={{
                              color: getPalette().statusColor.good.background,
                              verticalAlign: "center",
                              marginLeft: "0.5em"
                            }}/>
                          </Tooltip>
                        }
                        {status === 2 &&
                          <Tooltip title={`Rejected:${billetData.isCobble ? " Cobble" : ""} ${billetData.isCutUp ? "Cut up" : ""}`}>
                            <HighlightOffIcon style={{
                              color: getPalette().statusColor.error.background,
                              verticalAlign: "center",
                              marginLeft: "0.5em"
                            }}/>
                          </Tooltip>
                        }
                      </TableCell>
                      <TableCell align="center"  style={{ background: getRecipeBackground(billetData.recipe) }}>{billetData.recipe}</TableCell>
                      <TableCell align="center">{billetData.billet}</TableCell>
                      <TableCell align="right" style={{ background: compactingLength < 60 ? "green" : "red" }}>
                        {compactingLength.toFixed(1)}
                      </TableCell>
                      <TableCell align="right">{billetData.compactingPressure}</TableCell>
                      <TableCell align="right">{billetData.weight}</TableCell>

                      <TableCell align="center" style={style01(.1, tempStand4Errors)}>
                        {tempStand4Billet && tempStand4Billet.specMin && tempStand4Billet.specMin.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style01(.1, tempStand4Errors)}>
                        {tempStand4Billet && tempStand4Billet.specMax && tempStand4Billet.specMax.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style01(.1, tempStand4Errors)}>
                        {tempStand4Billet && tempStand4Billet.min.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style01(.1, tempStand4Errors)}>
                        {tempStand4Billet && tempStand4Billet.max.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style01(.1, tempStand4Errors)}>
                        {tempStand4Errors && <ErrorOutlineIcon />}
                        {tempStand4Billet && tempStand4Billet.count && (tempStand4Billet.sum / tempStand4Billet.count).toFixed(0)}
                      </TableCell>

                      <TableCell align="center" style={style02(.1, tempWaterBoxErrors)}>
                        {tempWaterBoxBillet && tempWaterBoxBillet.specMin && tempWaterBoxBillet.specMin.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style02(.1, tempWaterBoxErrors)}>
                        {tempWaterBoxBillet && tempWaterBoxBillet.specMax && tempWaterBoxBillet.specMax.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style02(.1, tempWaterBoxErrors)}>
                        {tempWaterBoxBillet && tempWaterBoxBillet.min.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style02(.1, tempWaterBoxErrors)}>
                        {tempWaterBoxBillet && tempWaterBoxBillet.max.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style02(.1, tempWaterBoxErrors)}>
                        {tempWaterBoxBillet && tempWaterBoxBillet.count && (tempWaterBoxBillet.sum / tempWaterBoxBillet.count).toFixed(0)}
                      </TableCell>

                      <TableCell align="center" style={style03(.1, tempLayingHeadErrors)}>
                        {tempLayingHeadBillet && tempLayingHeadBillet.specMin && tempLayingHeadBillet.specMin.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style03(.1, tempLayingHeadErrors)}>
                        {tempLayingHeadBillet && tempLayingHeadBillet.specMax && tempLayingHeadBillet.specMax.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style03(.1, tempLayingHeadErrors)}>
                        {tempLayingHeadBillet && tempLayingHeadBillet.min.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style03(.1, tempLayingHeadErrors)}>
                        {tempLayingHeadBillet && tempLayingHeadBillet.max.toFixed(0)}
                      </TableCell>
                      <TableCell align="center" style={style03(.1, tempLayingHeadErrors)}>
                        {tempLayingHeadBillet && tempLayingHeadBillet.count && (tempLayingHeadBillet.sum / tempLayingHeadBillet.count).toFixed(0)}
                      </TableCell>
                    </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </StyledTableRow>
      {openDetail.open && openDetail.item &&
        <Detail closed={() => setOpenDetail({ open: false, item: null })} fullWidth={true} maxWidth={"lg"}
          title={`Charts - Mill Order ${openDetail.item.millHeat} - ${openDetail.item.machine.name} - Billet ${openDetail.item.billet}`}>
          <ProductTrends productDetail={openDetail.item}/>
        </Detail>
      }
    </>
  );
};

const ProductionMachineRowComponent = ({ data }: IProductionMachineRowComponentProps): ReactElement => {
  const { productionMachine, billets } = data;
  const [open, setOpen] = useState(false);
  const tempStand4Strand = data.aggregate["Temp. Stand 4"];
  const tempWaterBoxStrand = data.aggregate["Temp. Waterbox"];
  const tempLayingHeadStrand = data.aggregate["Temp. Laying Head"];
  const tempStand4Errors = tempStand4Strand && tempStand4Strand.errors.length > 0;
  const tempWaterBoxErrors = tempWaterBoxStrand && tempWaterBoxStrand.errors.length > 0;
  const tempLayingHeadErrors = tempLayingHeadStrand && tempLayingHeadStrand.errors.length > 0;

  return (
    <>
      <StyledTableRow hover={true} role="checkbox" tabIndex={-1}>
        <TableCell align="center" style={{ position: "sticky", left: 0, ...style00(.1), width: 50 }}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)} >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell style={{ position: "sticky", left: 0, ...style00(.1) }}>
          {productionMachine.machine.name}
        </TableCell>
        <TableCell style={{ position: "sticky", left: 0, ...style00(.1) }}>
          {formatDate(productionMachine.shiftHistory.factoryDay)}
        </TableCell>
        <TableCell style={{ position: "sticky", left: 0, ...style00(.1) }}>
          {productionMachine.shiftHistory.name}
        </TableCell>
        <TableCell style={{ position: "sticky", left: 0, ...style00(.1) }}>
          {productionMachine.crew.name}
        </TableCell>

        <TableCell align="center" style={style01(.3, tempStand4Errors)}>
          {tempStand4Strand && tempStand4Strand.specMin && tempStand4Strand.specMin.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style01(.3, tempStand4Errors)}>
          {tempStand4Strand && tempStand4Strand.specMax && tempStand4Strand.specMax.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style01(.3, tempStand4Errors)}>
          {tempStand4Strand && tempStand4Strand.min.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style01(.3, tempStand4Errors)}>
          {tempStand4Strand && tempStand4Strand.max.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style01(.3, tempStand4Errors)}>
          {tempStand4Strand && tempStand4Strand.count && (tempStand4Strand.sum / tempStand4Strand.count).toFixed(0)}
        </TableCell>

        <TableCell align="center" style={style02(.3, tempWaterBoxErrors)}>
          {tempWaterBoxStrand && tempWaterBoxStrand.specMin && tempWaterBoxStrand.specMin.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style02(.3, tempWaterBoxErrors)}>
          {tempWaterBoxStrand && tempWaterBoxStrand.specMax && tempWaterBoxStrand.specMax.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style02(.3, tempWaterBoxErrors)}>
          {tempWaterBoxStrand && tempWaterBoxStrand.min.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style02(.3, tempWaterBoxErrors)}>
          {tempWaterBoxStrand && tempWaterBoxStrand.max.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style02(.3, tempWaterBoxErrors)}>
          {tempWaterBoxStrand && tempWaterBoxStrand.count && (tempWaterBoxStrand.sum / tempWaterBoxStrand.count).toFixed(0)}
        </TableCell>

        <TableCell align="center" style={style03(.3, tempLayingHeadErrors)}>
          {tempLayingHeadStrand && tempLayingHeadStrand.specMin && tempLayingHeadStrand.specMin.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style03(.3, tempLayingHeadErrors)}>
          {tempLayingHeadStrand && tempLayingHeadStrand.specMax && tempLayingHeadStrand.specMax.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style03(.3, tempLayingHeadErrors)}>
          {tempLayingHeadStrand && tempLayingHeadStrand.min.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style03(.3, tempLayingHeadErrors)}>
          {tempLayingHeadStrand && tempLayingHeadStrand.max.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style03(.3, tempLayingHeadErrors)}>
          {tempLayingHeadStrand && tempLayingHeadStrand.count && (tempLayingHeadStrand.sum / tempLayingHeadStrand.count).toFixed(0)}
        </TableCell>
      </StyledTableRow>
      <BilletsRowComponent billets={billets} open={open} />
    </>
  );
};

const RowComponent = ({ row }: IRowProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const tempStand4 = row.aggregate["Temp. Stand 4"];
  const tempWaterBox = row.aggregate["Temp. Waterbox"];
  const tempLayingHead = row.aggregate["Temp. Laying Head"];
  const tempStand4Errors = tempStand4 && tempStand4.errors.length > 0;
  const tempWaterBoxErrors = tempWaterBox && tempWaterBox.errors.length > 0;
  const tempLayingHeadErrors = tempLayingHead && tempLayingHead.errors.length > 0;

  return (
    <>
      <StyledTableRow hover={true} role="checkbox" tabIndex={-1}>
        <TableCell align="left" style={{ position: "sticky", left: 0, ...style00(.3), width: 50 }}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)} >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell style={{ position: "sticky", left: 0, ...style00(.3) }}>
          {row.millHeat}
        </TableCell>
        <TableCell style={{ position: "sticky", left: 0, ...style00(.3) }}>
          {row.heatNumber}
        </TableCell>
        <TableCell style={{ position: "sticky", left: 0, ...style00(.3) }}>
          {row.steelCode}
        </TableCell>

        <TableCell align="center" style={style01(.5, tempStand4Errors)}>
          {tempStand4 && tempStand4.specMin && tempStand4.specMin.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style01(.5, tempStand4Errors)}>
          {tempStand4 && tempStand4.specMax && tempStand4.specMax.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style01(.5, tempStand4Errors)}>
          {tempStand4 && tempStand4.min.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style01(.5, tempStand4Errors)}>
          {tempStand4 && tempStand4.max.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style01(.5, tempStand4Errors)}>
          {tempStand4 && tempStand4.count && (tempStand4.sum / tempStand4.count).toFixed(0)}
        </TableCell>

        <TableCell align="center" style={style02(.5, tempWaterBoxErrors)}>
          {tempWaterBox && tempWaterBox.specMin && tempWaterBox.specMin.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style02(.5, tempWaterBoxErrors)}>
          {tempWaterBox && tempWaterBox.specMax && tempWaterBox.specMax.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style02(.5, tempWaterBoxErrors)}>
          {tempWaterBox && tempWaterBox.min.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style02(.5, tempWaterBoxErrors)}>
          {tempWaterBox && tempWaterBox.max.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style02(.5, tempWaterBoxErrors)}>
          {tempWaterBox && tempWaterBox.count && (tempWaterBox.sum / tempWaterBox.count).toFixed(0)}
        </TableCell>

        <TableCell align="center" style={style03(.5, tempLayingHeadErrors)}>
          {tempLayingHead && tempLayingHead.specMin && tempLayingHead.specMin.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style03(.5, tempLayingHeadErrors)}>
          {tempLayingHead && tempLayingHead.specMax && tempLayingHead.specMax.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style03(.5, tempLayingHeadErrors)}>
          {tempLayingHead && tempLayingHead.min.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style03(.5, tempLayingHeadErrors)}>
          {tempLayingHead && tempLayingHead.max.toFixed(0)}
        </TableCell>
        <TableCell align="center" style={style03(.5, tempLayingHeadErrors)}>
          {tempLayingHead && tempLayingHead.count && (tempLayingHead.sum / tempLayingHead.count).toFixed(0)}
        </TableCell>
      </StyledTableRow>
      <StyledTableRow>
        <TableCell style={{ padding: 0, border: "none" }} colSpan={23} className="table-cell">
          <Collapse in={open} timeout="auto" unmountOnExit={true}>
            <Table stickyHeader={true} size={"small"} >
              <TableHead style={{ backgroundColor: "#b0bec5" }}>
                <TableRow >
                  <TableCell align="center" style={{ ...style00(.5), width: 50 }}/>
                  <TableCell align="center" style={style00(.5)}>Machine</TableCell>
                  <TableCell align="center" style={style00(.5)}>Factory Day</TableCell>
                  <TableCell align="center" style={style00(.5)}>Shift</TableCell>
                  <TableCell align="center" style={style00(.5)}>Crew</TableCell>

                  <TableCell align="center" style={style01(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style01(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style01(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style01(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style01(.5)}>AVG</TableCell>

                  <TableCell align="center" style={style02(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style02(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style02(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style02(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style02(.5)}>AVG</TableCell>

                  <TableCell align="center" style={style03(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style03(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style03(.5)}>MIN</TableCell>
                  <TableCell align="center" style={style03(.5)}>MAX</TableCell>
                  <TableCell align="center" style={style03(.5)}>AVG</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {row.productionMachines.map(pm => {

                  return (
                    <ProductionMachineRowComponent key={`pm-${pm.productionMachine.productionMachineID}`} data={pm}/>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </StyledTableRow>
    </>
  );
};

const ProductionQRTable = ({ productionData, products, parentSize }: IProps): ReactElement => {
  const [ dataProcessed, setDataProcessed ] = useState<IDataProcessed[] | null>(null);
  const [ getCatalogs ] = useAsyncFetch<Record<string, ICatalogCategory[]>>();
  const [ specs, setSpecs ] = useState<Record<string, ICatalogCategory[]>>({});
  const noSpecs = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const process = async  () => {
      const list = new Set<string>();

      for (const billet of products) {
        const catalogName = billet.recipe ?? "UNDEFINED";
        if (!specs[catalogName] && !noSpecs.current[catalogName]) {
          list.add(catalogName);
        }
      }

      if (list.size > 0) {
        const response = await getCatalogs({
          module: "catalog",
          query: "getCatalogsFromName",
          payload: {
            areaCode: "MILL",
            zoneCode: "WIREROD",
            catalogNames: [ ...list ]
          }
        });

        list.forEach(catalogName => {
          noSpecs.current[catalogName] = !(response && response[catalogName] && response[catalogName].length > 0);
        });

        setSpecs(prevState => ({ ...prevState, ...response }));
      }
    };

    process().then();
  }, [products, specs]);

  useEffect(() => {
    /// console.log(productionData);

    const cache: Record<string, IDataProcessed> = {};
    const productionMachineCache: Record<number, IProductionMachineData> = {};

    productionData.forEach(production => {
      const millHeat = production.reference ?? "UNDEFINED";

      cache[millHeat] = {
        heatNumber: "",
        steelCode: "",
        millHeat,
        productionMachines: [],
        aggregate: {}
      };

      production.productionMachines.forEach(productionMachine => {
        const object = {
          productionMachine: productionMachine as IStrandProduction,
          billets: [],
          aggregate: {}
        };

        productionMachineCache[productionMachine.productionMachineID] = object;
        cache[millHeat].productionMachines.push(object);
        cache[millHeat].heatNumber = object.productionMachine.heatNumber;
        cache[millHeat].steelCode = object.productionMachine.steelCode;
      });
    });

    for (const billet of products) {
      const catalogName = billet.recipe  ?? "UNDEFINED"; // "19 Fast CH 1/2 1080";
      const billetData: IBilletData = {
        ...billet,
        aggregateRecord: {}
      };

      let spec = specDictionary[catalogName];
      if (!spec) {
        const response = specs[catalogName];

        spec = response && response.length ? response[0].content.fields : [];
        specDictionary[catalogName] = spec;
      }

      productionMachineCache[billet.productionMachineId].billets.push(billetData);

      billetData.aggregate.forEach(a => {
        const field = spec.find(f => f.name === fieldMapping[a.code]);
        const error = evalError(evalAvg(a.count, a.sum), field?.minimum ?? null, field?.maximum ?? null);
        const specMin = field?.minimum ?? null;
        const specMax = field?.maximum ?? null;

        billetData.aggregateRecord[variableMapping[a.code]] = {
          min: a.min,
          max: a.max,
          sum: a.sum,
          count: a.count,
          specMin,
          specMax,
          errors: error ? [ `Billet ${billet.billet}: ${error}` ] : []
        };

        updateAggregate(
          variableMapping[a.code],
          productionMachineCache[billet.productionMachineId].aggregate,
          billetData.aggregateRecord[variableMapping[a.code]]);

        updateAggregate(
          variableMapping[a.code],
          cache[billet.millHeat].aggregate,
          billetData.aggregateRecord[variableMapping[a.code]]);
      });
    }

    setDataProcessed(Object.values(cache));
  }, [ products, specs ]);

  const onExportData = () => {
    if (dataProcessed) {
      const files: Record<string, Blob> = {
        orders: exportByOrder(dataProcessed),
        machines: exportByMachine(dataProcessed),
        billets: exportByBillets(dataProcessed)
      };

      saveZipAs(files, "qr-rm.zip").then();
    }
  };

  const tableContainerHeight = parentSize.height - 48;

  return (
    <>
      <TableContainer className="flex-grow-1" style={{ maxHeight: tableContainerHeight, height: tableContainerHeight }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead style={{ backgroundColor: "#b0bec5" }}>
            <TableRow>
              <TableCell align="center" style={style00(.5)} colSpan={4}/>
              <TableCell align="center" style={style01(.5)} colSpan={5}>Temp. Stand 4</TableCell>
              <TableCell align="center" style={style02(.5)} colSpan={5}>Temp. Waterbox</TableCell>
              <TableCell align="center" style={style03(.5)} colSpan={5}>Temp. Laying Head</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center" style={{ ...style00(.5), width: 50 }}/>
              <TableCell align="center" style={style00(.5)}>Order</TableCell>
              <TableCell align="center" style={style00(.5)}>Heat Number</TableCell>
              <TableCell align="center" style={style00(.5)}>Steel</TableCell>


              <TableCell align="center" style={style01(.5)}>MIN</TableCell>
              <TableCell align="center" style={style01(.5)}>MAX</TableCell>
              <TableCell align="center" style={style01(.5)}>MIN</TableCell>
              <TableCell align="center" style={style01(.5)}>MAX</TableCell>
              <TableCell align="center" style={style01(.5)}>AVG</TableCell>

              <TableCell align="center" style={style02(.5)}>MIN</TableCell>
              <TableCell align="center" style={style02(.5)}>MAX</TableCell>
              <TableCell align="center" style={style02(.5)}>MIN</TableCell>
              <TableCell align="center" style={style02(.5)}>MAX</TableCell>
              <TableCell align="center" style={style02(.5)}>AVG</TableCell>

              <TableCell align="center" style={style03(.5)}>MIN</TableCell>
              <TableCell align="center" style={style03(.5)}>MAX</TableCell>
              <TableCell align="center" style={style03(.5)}>MIN</TableCell>
              <TableCell align="center" style={style03(.5)}>MAX</TableCell>
              <TableCell align="center" style={style03(.5)}>AVG</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataProcessed && dataProcessed.map((row: IDataProcessed, index: number) => {

              return (
                <RowComponent key={`prod-eaf-row-${index}`} row={row} index={index} />
              );
            })}
          </TableBody>
        </Table>

        {!dataProcessed &&
          <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2">
            <CircularProgress className=""/>
          </Paper>
        }
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell>
              <IconButton color="primary" onClick={onExportData} disabled={!dataProcessed || dataProcessed.length === 0}>
                <Tooltip title={"Export data"}>
                  <FileDownloadIcon/>
                </Tooltip>
              </IconButton>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default ProductionQRTable;
